/**
 * DeferredParser is a class that allows to fetch an api endpoint right away, but parse the response later.
 * It is achieved by creating a promise that resolves with a parser function.
 *
 * So, you can call fetch() and parseWith() in any order.
 * When fetch will be completed and parser will be resolved, the parser will be called with the fetched data.
 * */
export class DeferredParser<FetchParams, ParsedData> {
  private readonly promiseToParse: Promise<(data: unknown) => ParsedData>;
  promiseToParseResolver: ((value: (data: unknown) => ParsedData) => void) | null = null;

  constructor(private fetcher: (params: FetchParams) => Promise<unknown>) {
    this.promiseToParse = new Promise((resolve) => {
      this.promiseToParseResolver = resolve;
    });
  }

  parseWith(parser: (data: unknown) => ParsedData): void {
    if (!this.promiseToParseResolver) {
      throw new Error('Promise to parse is already resolved');
    }

    this.promiseToParseResolver(parser);
  }

  async fetch(params: FetchParams): Promise<ParsedData> {
    const fetchPromise = this.fetcher(params);

    const [data, parser] = await Promise.all([fetchPromise, this.promiseToParse]);

    return parser(data);
  }
}
